<div
     snAssistantSurface="tool"
     i18n-surfaceTitle="@@tool.surface"
     surfaceTitle="Webhook tool" class="webhook-tool-container" [class.embedded]="isEmbedded">
  <!-- Top panel with breadcrumbs (only in standalone mode) -->
  @if (!isEmbedded) {
    <div class="webhook-top-panel">
    <div class="top-panel-container">
      <div class="panel-left-section">
        <p-breadcrumb 
          [model]="breadcrumbItems" 
          styleClass="custom-breadcrumb compact-breadcrumb"
          [style]="{'--p-breadcrumb-padding': '0', '--p-breadcrumb-item-hover-color': 'var(--p-primary-color)'}">
        </p-breadcrumb>
      </div>
    </div>
    </div>
  }

  <!-- Main content container -->
  <div class="webhook-tool-page-container">
    @if (!isEmbedded) {
      <!-- Standalone mode - with card -->
      <p-card class="webhook-tool-page-container-card">
        <ng-container *ngTemplateOutlet="webhookContent"></ng-container>
      </p-card>
    } @else {
      <!-- Embedded mode - without card -->
      <div class="embedded-content">
        <ng-container *ngTemplateOutlet="webhookContent"></ng-container>
      </div>
    }
  </div>
</div>

<!-- Webhook Content Template -->
<ng-template #webhookContent>
  <!-- Header section -->
  <div class="header-section">
    <h2>{{ headerTitle }}</h2>
    <p class="header-description" i18n="@@webhookTool.description">Configure a webhook tool to interact with external services.</p>
  </div>

  <!-- Loading spinner -->
  @if (isLoading) {
    <div class="loading-container">
      <p-progressSpinner strokeWidth="3" [style]="{'width': '50px', 'height': '50px'}"></p-progressSpinner>
      <p i18n="@@webhookTool.loading">Loading tool...</p>
    </div>
  }

  <!-- Form content -->
  @if (!isLoading) {
    <div class="webhook-form-container">
      <ng-container *ngTemplateOutlet="formFields"></ng-container>
    </div>
  }

  <!-- Desktop Action Buttons -->
  <div class="action-buttons">
    <p-button
      type="button"
      i18n-label="@@common.back"
      label="Back"
      severity="secondary"
      [outlined]="true"
      (click)="onCancel()"
      icon="pi pi-arrow-left">
    </p-button>
    <div class="action-buttons-right">
      @if (isUpdateMode) {
        <p-button
          type="button"
          i18n-label="@@webhookTool.testTool"
          label="Test Tool"
          severity="secondary"
          [outlined]="true"
          (click)="onTestTool()"
          icon="pi pi-play"
          [disabled]="isTestingTool"
          [loading]="isTestingTool">
        </p-button>
      }
      <p-button
        type="button"
        [label]="saveChangesBtnLabel"
        snAssistantAction="save"
        actionHint="Saves the webhook tool. Edits do not persist until this runs."
        (click)="onSave()"
        [icon]="isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
        [disabled]="isSaveDisabled"
        [loading]="isSaving">
      </p-button>
    </div>
  </div>

  <!-- Mobile Bottom Toolbar with PrimeNG -->
  <p-toolbar class="mobile-bottom-toolbar">
    <ng-template pTemplate="start">
      <p-button
        type="button"
        i18n-label="@@common.back"
        label="Back"
        severity="secondary"
        [outlined]="true"
        (click)="onCancel()"
        icon="pi pi-arrow-left"
        size="small">
      </p-button>
    </ng-template>

    <ng-template pTemplate="end">
      @if (isUpdateMode) {
        <p-button
          type="button"
          i18n-label="@@webhookTool.test"
          label="Test"
          severity="secondary"
          [outlined]="true"
          (click)="onTestTool()"
          icon="pi pi-play"
          [disabled]="isTestingTool"
          [loading]="isTestingTool"
          size="small">
        </p-button>
      }
      <p-button
        type="button"
        [label]="saveChangesBtnLabel"
        (click)="onSave()"
        [icon]="isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'"
        [disabled]="isSaveDisabled"
        [loading]="isSaving"
        size="small">
      </p-button>
    </ng-template>
  </p-toolbar>
</ng-template>

<!-- Form fields template -->
<ng-template #formFields>
  <!-- Tool Name Section -->
  <div class="form-field">
    <label class="field-label"><ng-container i18n="@@webhookTool.name.label">Tool Name</ng-container> *</label>
    <p class="field-description" i18n="@@webhookTool.name.description">A unique name for this webhook tool.</p>
    <input
      [snAssistantId]="F.title"
      pInputText
      name="title"
      [(ngModel)]="tool.title"
      (ngModelChange)="onFieldChange('title')"
      i18n-placeholder="@@webhookTool.name.placeholder"
      placeholder="e.g., Weather API"
      class="w-full">
  </div>

  <!-- Description Section -->
  <div class="form-field">
    <label class="field-label" i18n="@@webhookTool.description.label">Description</label>
    <p class="field-description" i18n="@@webhookTool.description.desc">Brief description of what this tool does.</p>
    <textarea
      [snAssistantId]="F.description"
      pTextarea
      name="description"
      [(ngModel)]="tool.description"
      (ngModelChange)="onFieldChange('description')"
      i18n-placeholder="@@webhookTool.description.placeholder"
      placeholder="e.g., Fetches current weather information"
      rows="3"
      [autoResize]="true"
      class="tool-description-textarea">
    </textarea>
  </div>

  <!-- HTTP Method Section -->
  <div class="form-field">
    <label class="field-label"><ng-container i18n="@@webhookTool.method.label">HTTP Method</ng-container> *</label>
    <p class="field-description" i18n="@@webhookTool.method.description">The HTTP method to use for the request.</p>
    <p-select
      [snAssistantId]="F.method"
      name="method"
      [(ngModel)]="tool.method"
      [options]="httpMethodOptions"
      optionLabel="label"
      optionValue="value"
      (ngModelChange)="onFieldChange('method')"
      i18n-placeholder="@@webhookTool.method.placeholder"
      placeholder="Select HTTP method"
      styleClass="w-full">
    </p-select>
  </div>

  <!-- URL Section -->
  <div class="form-field">
    <label class="field-label">URL *</label>
    <p class="field-description" i18n="@@webhookTool.url.description">The endpoint URL that will receive the webhook request.</p>
    <input
      [snAssistantId]="F.url"
      pInputText
      name="url"
      [(ngModel)]="tool.url"
      (ngModelChange)="onFieldChange('url')"
      placeholder="https://api.example.com/webhook"
      class="w-full">
  </div>

  <!-- Headers Section -->
  <div class="form-field">
    <!-- Схемы запроса/ответа: структура, а не значение — редактируется
           своим редактором, агенту пока не отдаём. -->
      <app-openapi-properties-editor
        snAssistantSkip
      [properties]="headersProperties"
      [readonlyProperties]="[]"
      [allowLlmFill]="true"
      [allowedTypes]="['string']"
      [title]="headersTitle"
      (propertiesChange)="onHeadersPropertiesChange($event)">
    </app-openapi-properties-editor>
  </div>

  <!-- URL Parameters Section -->
  <div class="form-field">
    <!-- Схемы запроса/ответа: структура, а не значение — редактируется
           своим редактором, агенту пока не отдаём. -->
      <app-openapi-properties-editor
        snAssistantSkip
      [properties]="urlParametersProperties"
      [readonlyProperties]="[]"
      [allowLlmFill]="true"
      [allowedTypes]="['string', 'integer', 'number']"
      [title]="urlParametersTitle"
      (propertiesChange)="onUrlParametersPropertiesChange($event)">
    </app-openapi-properties-editor>
  </div>

  <!-- Body Section (only for non-GET methods) -->
  @if (tool.method !== 'GET') {
    <div class="form-field">
      <!-- Схемы запроса/ответа: структура, а не значение — редактируется
           своим редактором, агенту пока не отдаём. -->
      <app-openapi-properties-editor
        snAssistantSkip
        [properties]="bodyProperties"
        [readonlyProperties]="[]"
        [allowLlmFill]="true"
        [allowedTypes]="['string', 'integer', 'number', 'boolean', 'enum', 'object', 'array']"
        [title]="bodyTitle"
        (propertiesChange)="onBodyPropertiesChange($event)">
      </app-openapi-properties-editor>
    </div>
  }

  <!-- Request Preview Section -->
  <div class="form-field">
    <div class="curl-preview-header">
      <div>
        <label class="field-label" i18n="@@webhookTool.curl.label">Request Preview (cURL)</label>
        <p class="field-description" i18n="@@webhookTool.curl.description">Preview of the HTTP request in cURL format</p>
      </div>
      <p-button
        icon="pi pi-copy"
        severity="secondary"
        [outlined]="true"
        size="small"
        (click)="copyCurlToClipboard()"
        i18n-pTooltip="@@common.copyToClipboard"
        pTooltip="Copy to clipboard"
        tooltipPosition="left"
        i18n-ariaLabel="@@common.copyToClipboard"
        ariaLabel="Copy to clipboard">
      </p-button>
    </div>
    <pre class="curl-preview">{{ getCurlPreview() }}</pre>
  </div>

  <!-- Timeout Section -->
  <div class="form-field">
    <label class="field-label" i18n="@@webhookTool.timeout.label">Timeout (seconds)</label>
    <p class="field-description" i18n="@@webhookTool.timeout.description">Request timeout in seconds.</p>
    <input
      [snAssistantId]="F.responseTimeoutSeconds"
      pInputText
      type="number"
      name="response_timeout_seconds"
      [(ngModel)]="tool.response_timeout_seconds"
      (ngModelChange)="onFieldChange('response_timeout_seconds')"
      placeholder="30"
      min="1"
      max="300"
      class="w-full">
  </div>

  <!-- Disable Interruptions Section -->
  <div class="form-field">
    <div class="switch-field">
      <div class="switch-content">
        <label class="switch-label" i18n="@@webhookTool.disableInterruptions.label">Disable Interruptions</label>
        <p class="field-description" i18n="@@webhookTool.disableInterruptions.description">When enabled, prevents interruptions during tool execution</p>
      </div>
      <p-toggleSwitch
        [snAssistantId]="F.disableInterruptions"
        name="disable_interruptions"
        [(ngModel)]="tool.disable_interruptions"
        (ngModelChange)="onFieldChange('disable_interruptions')">
      </p-toggleSwitch>
    </div>
  </div>

  <!-- Async (agent-binding only) — visible when this editor is embedded
       inside agent-edit; toggles agent.tools[].async, persisted with the
       agent save, not the tool save. -->
  @if (agentTool) {
    <div class="form-field">
      <div class="switch-field">
        <div class="switch-content">
          <label class="switch-label" i18n="@@webhookTool.async.label">Асинхронный вызов</label>
          <p class="field-description" i18n="@@webhookTool.async.description">
            Когда включено, агент не ждёт ответа инструмента — продолжает диалог, а результат подмешается в разговор, когда придёт. Полезно для долгих вызовов, чтобы не было неловкой паузы.
          </p>
        </div>
        <p-toggleSwitch
          [ngModel]="!!agentTool.async"
          (ngModelChange)="agentTool.async = $event">
        </p-toggleSwitch>
      </div>
    </div>
  }

  <!-- Pre-tool Speech Section -->
  <div class="form-field">
    <label class="field-label" i18n="@@webhookTool.preSpeech.label">Pre-tool Speech</label>
    <p class="field-description" i18n="@@webhookTool.preSpeech.description">Message to speak before executing the tool (optional)</p>
    <textarea
      [snAssistantId]="F.preToolSpeech"
      pTextarea
      name="pre_tool_speech"
      [(ngModel)]="tool.pre_tool_speech"
      (ngModelChange)="onFieldChange('pre_tool_speech')"
      i18n-placeholder="@@webhookTool.preSpeech.placeholder"
      placeholder="e.g., Let me check that for you..."
      rows="2"
      [autoResize]="true"
      class="w-full">
    </textarea>
  </div>

  <!-- Progress Speech Section -->
  <div class="form-field">
    <label class="field-label" i18n="@@webhookTool.progressSpeech.label">Progress Speech</label>
    <p class="field-description" i18n="@@webhookTool.progressSpeech.description">Message to speak periodically during tool execution (optional)</p>
    <textarea
      [snAssistantId]="F.progressSpeech"
      pTextarea
      name="progress_speech"
      [(ngModel)]="tool.progress_speech"
      (ngModelChange)="onFieldChange('progress_speech')"
      i18n-placeholder="@@webhookTool.progressSpeech.placeholder"
      placeholder="e.g., Still working on it..."
      rows="2"
      [autoResize]="true"
      class="w-full">
    </textarea>
  </div>

  <!-- Progress Speech Interval Section -->
  <div class="form-field">
    <label class="field-label" i18n="@@webhookTool.progressInterval.label">Progress Speech Interval (seconds)</label>
    <p class="field-description" i18n="@@webhookTool.progressInterval.description">How often to speak the progress message (optional)</p>
    <input
      [snAssistantId]="F.progressSpeechIntervalSeconds"
      pInputText
      type="number"
      name="progress_speech_interval_seconds"
      [(ngModel)]="tool.progress_speech_interval_seconds"
      (ngModelChange)="onFieldChange('progress_speech_interval_seconds')"
      placeholder="5"
      min="1"
      max="60"
      class="w-full">
  </div>
</ng-template>